import * as React from "react";
import {Route} from "react-router-dom";

import {redirect} from "@repo/urls";

import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";

type Props = {
    /**
     * The path that the redirect redirects from.
     *
     * This is for use within a route Switch inside React Router.
     */
    readonly from?: string;
    /**
     * When `true`, a `from` path that has a trailing slash will only match a
     * `location.pathname` with a trailing slash.
     * This has no effect when there are additional URL segments in the
     * `location.pathname` or `from` is not specified.
     *
     * Ignored if `from` is not specified.
     */
    readonly strict?: boolean;
    /**
     * When `true`, `from` will only match if the path matches the
     * `location.pathname` exactly.
     *
     * Ignored if `from` is not specified.
     */
    readonly exact?: boolean;
    /**
     * When `true`, `from` is matched in a case-sensitive manner.
     *
     * Ignored if `from` is not specified.
     */
    readonly sensitive?: boolean;
    /**
     * The target URL for the redirect.
     */
    readonly to: string;
};

/**
 * This is the actual redirect rendering.
 */
const renderRedirect = (to: string): React.ReactNode => {
    redirect(to);
    return <RouteLoadSpinner />;
};

/**
 * Render a full page redirect.
 *
 * This is a full redirect in that the entire page is reloaded.
 */
export default ({from, to}: Props): React.ReactNode => {
    if (from != null) {
        /**
         * We have a `from` prop so we need to render as a Route in a route
         * table. Not because the route table will pick it up, but because
         * it means we track the `path` in our route testing.
         */
        return <Route path={from} render={() => renderRedirect(to)} />;
    }

    /**
     * We are not in a routing table, or we don't care about whether we are
     * so, just render the redirect.
     */
    return renderRedirect(to);
};
